import { useEffect, useMemo, useRef, useState } from 'react';
import { Loader2, Mail, UserPlus, UserCircle2, Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { inviteWorkspaceMember } from '@/lib/database';

export interface DirectoryUser {
  id: string;
  email: string;
  name: string | null;
}

type Role = 'admin' | 'reader';

type Selection =
  | { kind: 'existing'; user: DirectoryUser }
  | { kind: 'invite'; email: string };

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface AddMemberDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workspaceId: string;
  workspaceName: string;
  directory: DirectoryUser[];
  existingMemberIds: Set<string>;
  pendingInviteEmails: Set<string>;
  onAdded: () => void | Promise<void>;
}

export function AddMemberDialog({
  open,
  onOpenChange,
  workspaceId,
  workspaceName,
  directory,
  existingMemberIds,
  pendingInviteEmails,
  onAdded,
}: AddMemberDialogProps) {
  const [query, setQuery] = useState('');
  const [selection, setSelection] = useState<Selection | null>(null);
  const [role, setRole] = useState<Role>('reader');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) {
      setQuery('');
      setSelection(null);
      setRole('reader');
      setError(null);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  const normalizedQuery = query.trim().toLowerCase();
  const queryIsEmail = EMAIL_RE.test(normalizedQuery);

  const matches = useMemo(() => {
    if (!normalizedQuery) return directory.slice(0, 8);
    return directory
      .filter((u) => {
        const inEmail = u.email.toLowerCase().includes(normalizedQuery);
        const inName = u.name?.toLowerCase().includes(normalizedQuery) ?? false;
        return inEmail || inName;
      })
      .slice(0, 12);
  }, [directory, normalizedQuery]);

  const exactEmailMatch = useMemo(
    () =>
      queryIsEmail
        ? directory.find((u) => u.email.toLowerCase() === normalizedQuery)
        : undefined,
    [directory, normalizedQuery, queryIsEmail]
  );

  const showInviteOption = queryIsEmail && !exactEmailMatch;

  const handleSelectExisting = (user: DirectoryUser) => {
    if (existingMemberIds.has(user.id)) return;
    setSelection({ kind: 'existing', user });
    setQuery(user.email);
    setError(null);
  };

  const handleSelectInvite = () => {
    if (pendingInviteEmails.has(normalizedQuery)) return;
    setSelection({ kind: 'invite', email: normalizedQuery });
    setError(null);
  };

  const submitDisabled = !selection || submitting;

  const submitLabel = (() => {
    if (!selection) return 'Add member';
    if (selection.kind === 'existing') return 'Add to workspace';
    return 'Send invitation';
  })();

  const handleSubmit = async () => {
    if (!selection) return;
    const email =
      selection.kind === 'existing' ? selection.user.email : selection.email;
    setSubmitting(true);
    setError(null);
    try {
      await inviteWorkspaceMember(workspaceId, email, role);
      onOpenChange(false);
      await onAdded();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add member');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserPlus className="h-5 w-5" />
            Add member to {workspaceName}
          </DialogTitle>
          <DialogDescription>
            Search for an existing user by name or email, or invite someone new
            by typing their email address.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div>
            <Input
              ref={inputRef}
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setSelection(null);
              }}
              placeholder="Search name or email, or type a new email"
              autoComplete="off"
            />
          </div>

          <div className="border rounded-md max-h-64 overflow-y-auto">
            {matches.length === 0 && !showInviteOption && (
              <div className="px-4 py-6 text-center text-sm text-gray-500">
                {normalizedQuery
                  ? 'No matches. Type a full email to invite a new user.'
                  : 'Start typing to search users.'}
              </div>
            )}

            {matches.map((user) => {
              const isMember = existingMemberIds.has(user.id);
              const isInvited = pendingInviteEmails.has(user.email.toLowerCase());
              const isSelected =
                selection?.kind === 'existing' && selection.user.id === user.id;
              const disabled = isMember || isInvited;

              return (
                <button
                  key={user.id}
                  type="button"
                  disabled={disabled}
                  onClick={() => handleSelectExisting(user)}
                  className={`w-full text-left px-3 py-2 flex items-center gap-3 border-b last:border-b-0 transition-colors ${
                    disabled
                      ? 'opacity-60 cursor-not-allowed'
                      : 'hover:bg-gray-50 cursor-pointer'
                  } ${isSelected ? 'bg-blue-50' : ''}`}
                >
                  <UserCircle2 className="h-5 w-5 text-gray-400 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">
                      {user.name || user.email}
                    </div>
                    {user.name && (
                      <div className="text-xs text-gray-500 truncate">
                        {user.email}
                      </div>
                    )}
                  </div>
                  {isMember && (
                    <span className="text-xs text-gray-500 shrink-0">
                      Already a member
                    </span>
                  )}
                  {!isMember && isInvited && (
                    <span className="text-xs text-amber-600 shrink-0">
                      Invitation pending
                    </span>
                  )}
                </button>
              );
            })}

            {showInviteOption && (
              <button
                type="button"
                onClick={handleSelectInvite}
                disabled={pendingInviteEmails.has(normalizedQuery)}
                className={`w-full text-left px-3 py-2 flex items-center gap-3 border-t bg-gray-50 transition-colors ${
                  pendingInviteEmails.has(normalizedQuery)
                    ? 'opacity-60 cursor-not-allowed'
                    : 'hover:bg-gray-100 cursor-pointer'
                } ${
                  selection?.kind === 'invite' &&
                  selection.email === normalizedQuery
                    ? 'bg-blue-50'
                    : ''
                }`}
              >
                <Mail className="h-5 w-5 text-blue-600 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">
                    Invite{' '}
                    <span className="font-mono">{normalizedQuery}</span>
                  </div>
                  <div className="text-xs text-gray-500">
                    They'll join the workspace when they sign up.
                  </div>
                </div>
                {pendingInviteEmails.has(normalizedQuery) && (
                  <span className="text-xs text-amber-600 shrink-0">
                    Already pending
                  </span>
                )}
              </button>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Role</label>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setRole('reader')}
                className={`px-3 py-2 rounded-md border text-sm text-left ${
                  role === 'reader'
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:bg-gray-50'
                }`}
              >
                <div className="font-medium">Reader</div>
                <div className="text-xs text-gray-500">View-only access</div>
              </button>
              <button
                type="button"
                onClick={() => setRole('admin')}
                className={`px-3 py-2 rounded-md border text-sm text-left ${
                  role === 'admin'
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:bg-gray-50'
                }`}
              >
                <div className="font-medium">Admin</div>
                <div className="text-xs text-gray-500">
                  Manage members, billing, projects
                </div>
              </button>
            </div>
          </div>

          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">
              {error}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={submitDisabled}>
            {submitting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Working…
              </>
            ) : (
              <>
                <Send className="h-4 w-4 mr-2" />
                {submitLabel}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
